import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Box, Container, Alert, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { supabase } from '../../config/supabase';

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: 'Poppins, Arial, sans-serif',
  },
});

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [isValidSession, setIsValidSession] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);

  useEffect(() => {
    console.log('🚀 Initializing password reset - checking for valid session...');
    console.log('Current URL:', window.location.href);
    console.log('URL search params:', window.location.search);
    console.log('URL hash:', window.location.hash);
    
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        console.log('Session check:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userEmail: session?.user?.email
        });
        
        if (session?.user) {
          setSession(session);
          setIsValidSession(true);
          console.log("✅ Valid session found for password reset");
        } else {
          // Check if this is a direct landing with tokens (legacy flow)
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const token = urlParams.get('token') || hashParams.get('token');
          const isRecovery = urlParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
          
          if (token && isRecovery) {
            console.log("🔄 Direct landing with token detected, verifying token...");
            setSessionLoading(true);
            
            try {
              // Verify the token to establish session
              const { data, error } = await supabase.auth.verifyOtp({
                token_hash: token,
                type: 'recovery'
              });
              
              if (error) {
                console.error("Token verification failed:", error);
                setIsValidSession(false);
                setError("Invalid or expired reset link. Please request a new password reset from the login page.");
                setSessionLoading(false);
                return;
              }
              
              console.log("✅ Token verification successful, session established");
              
              // Get the new session after token verification
              const { data: sessionData } = await supabase.auth.getSession();
              if (sessionData.session) {
                setSession(sessionData.session);
                setIsValidSession(true);
                console.log("✅ Session established after token verification");
              } else {
                setIsValidSession(false);
                setError("Failed to establish session after token verification.");
              }
            } catch (err) {
              console.error('Error verifying token:', err);
              setError("Unable to verify reset token. Please try requesting a new password reset.");
              setIsValidSession(false);
            } finally {
              setSessionLoading(false);
            }
            return;
          }
          
          // If someone navigated here directly without session or tokens
          setIsValidSession(false);
          setError("Invalid or expired reset link. Please request a new password reset from the login page.");
          console.log("❌ No valid session - user must request new reset link");
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setError("Unable to verify reset session. Please try requesting a new password reset.");
        setIsValidSession(false);
      } finally {
        setSessionLoading(false);
      }
    };
    
    init();
  }, []);

  const navigate = useNavigate();
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    console.log('🔄 Starting password reset process...');
    
    // Double-check we have a valid session before proceeding
    if (!isValidSession || !session) {
      setError("Session expired. Please request a new password reset link.");
      return;
    }
    
    if (password !== confirmPassword) {
      console.log('❌ Passwords do not match');
      return setError("Passwords don't match");
    }
    
    if (password.length < 6) {
      console.log('❌ Password too short');
      return setError("Password must be at least 6 characters");
    }
    
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      console.log('🔄 Updating password with authenticated session');
      
      // Update password using the authenticated session
      const { error } = await supabase.auth.updateUser({ 
        password: password
      });
      
      console.log('Password update response:', { 
        hasError: !!error,
        errorMessage: error?.message
      });
      
      if (error) {
        if (error.message?.includes('session')) {
          throw new Error('Your password reset session has expired. Please request a new password reset email.');
        } else {
          throw new Error(error.message || 'Failed to reset password');
        }
      }
      
      console.log('✅ Password reset successful!');
      setMessage('Password updated successfully! You will be redirected to login shortly.');
      
      // Sign the user out after password reset for security
      await supabase.auth.signOut();
      
      // Redirect to login page after successful reset
      setTimeout(() => {
        console.log('🔄 Redirecting to login...');
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      console.error('❌ Password reset error:', error);
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Reset Your Password
          </Typography>

          {sessionLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Verifying reset session...
              </Typography>
            </Box>
          )}

          {!sessionLoading && !isValidSession && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error || "Invalid or expired reset link. Please request a new password reset."}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" color="primary">
                    Back to Login
                  </Button>
                </Link>
              </Box>
            </Box>
          )}

          {!sessionLoading && isValidSession && (
            <>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {message && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {message}
                </Alert>
              )}

              <Box component="form" onSubmit={handleResetPassword} noValidate sx={{ mt: 1, width: '100%' }}>
                {/* Hidden username field for password managers */}
                <input 
                  type="text" 
                  autoComplete="username" 
                  style={{ display: 'none' }} 
                  aria-hidden="true"
                  value={session?.user?.email || "user@example.com"}
                  readOnly
                  tabIndex={-1}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="New Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{ minLength: 6 }}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  inputProps={{ minLength: 6 }}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.2,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #5a71d9 0%, #6a4294 100%)',
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                    Remember your password? Sign in
                  </Typography>
                </Link>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
